import { useCallback, useMemo, useSyncExternalStore } from 'react';

/**
 * 미디어 쿼리 하나를 구독합니다.
 *
 * effect + setState 대신 useSyncExternalStore를 쓰는 이유는 첫 렌더에서 바로 맞는
 * 값을 읽기 때문입니다. effect로 하면 모바일에서도 한 프레임은 데스크톱 배치로 그려진
 * 뒤에 교정되면서 UI가 자리를 옮기며 깜빡입니다.
 *
 * @param query matchMedia에 그대로 넘길 쿼리. 렌더마다 같은 문자열이어야 합니다.
 * @returns matchMedia가 없는 환경(SSR, jsdom 일부)에서는 false.
 */
export function useMediaQuery(query: string): boolean {
  const mediaQueryList = useMemo(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return null;
    }
    return window.matchMedia(query);
  }, [query]);

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (mediaQueryList === null) {
        return () => undefined;
      }

      if (typeof mediaQueryList.addEventListener === 'function') {
        mediaQueryList.addEventListener('change', onStoreChange);
        return () => mediaQueryList.removeEventListener('change', onStoreChange);
      }

      // Safari 13 이하의 구형 MediaQueryList API fallback입니다(useColorTheme과 동일).
      mediaQueryList.addListener(onStoreChange);
      return () => mediaQueryList.removeListener(onStoreChange);
    },
    [mediaQueryList]
  );

  const getSnapshot = useCallback(() => mediaQueryList?.matches ?? false, [mediaQueryList]);

  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}
